import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  useToast,
  UseToastOptions,
  VStack,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { AsyncSelect, Select, MultiValue, SingleValue } from 'chakra-react-select';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useMemo, useState, useCallback } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { RouterOutputs } from '@duke-hq/svc-api/provider';

import { useAuthTenant } from '../useAuthTenant';
import { skipToken } from '@tanstack/react-query';
import {
  type ConsultantRoleType,
  ConsultantRoleSchema,
  getRolesAvailable,
  ConsultantSelectOption,
} from '@duke-hq/svc-api/provider/util/consultants';
import { DUKE_DOMAIN, emailHasAllowedDomain } from '@duke-hq/shared-constants/emails';
import { useProviderEmailDomains } from '../hooks/useProviderEmailDomains';

interface TeamMemberFormData {
  id?: string;
  email: string;
  first_name: string;
  last_name: string;
  role: ConsultantRoleType | null;
  region?: string | null;
  site?: string | null;
  deleted_at?: Date | null;
}

type SelectOption = {
  label: string;
  value: string;
};

const defaultFormState: TeamMemberFormData = {
  id: undefined,
  email: '',
  first_name: '',
  last_name: '',
  role: null,
  region: null,
  site: null,
};

export function TeamMemberForm({
  member,
  allowRoleSelection = true,
  onClose,
  readOnly = false,
}: {
  member?: NonNullable<RouterOutputs['users']['consultantById']>;
  allowRoleSelection?: boolean;
  onClose?: () => void;
  readOnly?: boolean;
}): React.ReactElement {
  const [roleSelected, setRoleSelected] = useState<ConsultantRoleType | null>(null);
  const [isStateOrRegionRoleSelected, setIsStateOrRegionRoleSelected] = useState<boolean>(false);
  const [isSiteOrConsultantRoleSelected, setIsSiteOrConsultantRoleSelected] =
    useState<boolean>(false);

  const [showRoleLocationGroupSelector, setShowRoleLocationGroupSelector] =
    useState<boolean>(!member);
  const [emailToValidate, setEmailToValidate] = useState<boolean>(false);
  const allowDukeDomain = !!(
    member &&
    typeof member.email === 'string' &&
    typeof DUKE_DOMAIN === 'string' &&
    member.email.endsWith(DUKE_DOMAIN)
  );

  const auth = useAuthTenant();
  const { allowedDomains, defaultDomain } = useProviderEmailDomains();
  const dropdownDomains = useMemo(
    () => allowedDomains.filter((domain) => domain !== DUKE_DOMAIN || domain === defaultDomain),
    [allowedDomains, defaultDomain],
  );
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);

  const memberDomain = useMemo(() => {
    if (member?.email != null && member.email.includes('@')) {
      return member.email.split('@')[1];
    }
    return null;
  }, [member?.email]);

  const emailDomain = useMemo(() => {
    if (member?.email?.endsWith(DUKE_DOMAIN) === true) {
      return DUKE_DOMAIN;
    }

    return selectedDomain ?? memberDomain ?? defaultDomain ?? auth?.user.tenant.email_domain;
  }, [member?.email, selectedDomain, memberDomain, defaultDomain, auth?.user.tenant.email_domain]);

  const {
    handleSubmit,
    register,
    control,
    clearErrors,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting, isLoading },
  } = useForm<TeamMemberFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: member
      ? {
          id: member.id,
          email: member?.email?.split('@')[0],
          first_name: member.first_name,
          last_name: member.last_name ?? '',
          role: member.role,
          region: null,
          site: null,
        }
      : {
          ...defaultFormState,
        },
  });

  const toast = useToast();
  const utils = trpcTenant.useUtils();
  const email = watch('email');

  const roleOptions = useMemo(() => getRolesAvailable(auth.user.role), [auth.user.role]);

  // When in read-only mode, get all roles to display the member's role correctly
  const allRoleOptions = useMemo(() => getRolesAvailable(), []);

  const createMember = trpcTenant.users.createConsultant.useMutation({
    onSuccess() {
      toast({ ...toastMessage });
      reset();
      void utils.users.listMembersWithPagination.refetch();
    },
  });

  const updateMember = trpcTenant.users.updateConsultant.useMutation({
    onSuccess: () => {
      toast({ ...toastMessage });
      void utils.users.listMembersWithPagination.refetch();
      void utils.users.consultantById.refetch();
    },
  });

  const { data: siteRegions, isPending: isRegionsPending } =
    trpcTenant.locations.listSiteRegions.useQuery(
      {},
      {
        enabled: Boolean(
          roleSelected &&
          (roleSelected === ConsultantRoleSchema.enum.STATE_MANAGER ||
            roleSelected === ConsultantRoleSchema.enum.REGIONAL_MANAGER),
        ),
      },
    );

  const siteRegionsOptions = useMemo(() => {
    return (
      siteRegions?.map((region) => ({
        label: region.name,
        value: region.id,
      })) || []
    );
  }, [siteRegions]);

  const loadSiteRegionsOptions = useDebounceCallback<
    (
      inputValue: string,
      callback: (
        opts: {
          value: string;
          label: string;
        }[],
      ) => void,
    ) => void
  >((inputValue: string, callback) => {
    if (siteRegionsOptions.length > 0) {
      callback(
        siteRegionsOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const { data: siteLocations, isPending: isSitesPending } =
    trpcTenant.locations.listSiteLocations.useQuery(
      {
        filters: {
          available: true,
        },
      },
      {
        enabled: Boolean(
          roleSelected &&
          (roleSelected === ConsultantRoleSchema.enum.SITE_MANAGER ||
            roleSelected === ConsultantRoleSchema.enum.CONSULTANT),
        ),
      },
    );

  const sitesLocationsOptions = useMemo(() => {
    return (
      siteLocations?.map((site) => ({
        value: site.id,
        label: site.state ? `${site.name}, ${site.state}` : site.name,
      })) || []
    );
  }, [siteLocations]);

  const loadSitesLocationsOptions = useDebounceCallback<
    (
      inputValue: string,
      callback: (
        opts: {
          value: string;
          label: string;
        }[],
      ) => void,
    ) => void
  >((inputValue: string, callback) => {
    if (sitesLocationsOptions.length > 0) {
      callback(
        sitesLocationsOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const { refetch: checkEmail } = trpcTenant.users.searchUser.useQuery(
    email
      ? {
          email: allowDukeDomain ? `${email}@${DUKE_DOMAIN}` : `${email}@${emailDomain}`,
        }
      : skipToken,
    {
      enabled: Boolean(emailToValidate),
    },
  );

  const toastMessage: UseToastOptions = {
    title: member ? t`Team member updated successfully.` : t`Team member invited successfully.`,
    status: 'success',
    duration: 7000,
    isClosable: true,
  };

  const validateEmail = useCallback(async () => {
    if (email == null) {
      return true;
    }

    // Skip validation if site prop is passed and name is unchanged
    if (member?.email != null && member.email.length > 0 && email === member.email.split('@')[0]) {
      return true;
    }

    setEmailToValidate(true);

    const result = await checkEmail();
    setEmailToValidate(false);

    return result.data === false;
  }, [email, member, checkEmail]);

  const handleRoleChange = useCallback(
    (role: ConsultantRoleType) => {
      setRoleSelected(role);
      setIsStateOrRegionRoleSelected(false);
      setIsSiteOrConsultantRoleSelected(false);

      if (
        role === ConsultantRoleSchema.enum.STATE_MANAGER ||
        role === ConsultantRoleSchema.enum.REGIONAL_MANAGER
      ) {
        setIsStateOrRegionRoleSelected(true);
        setIsSiteOrConsultantRoleSelected(false);

        // Clear the values when the role changes
        setValue('site', null);
      } else if (
        role === ConsultantRoleSchema.enum.SITE_MANAGER ||
        role === ConsultantRoleSchema.enum.CONSULTANT
      ) {
        setIsSiteOrConsultantRoleSelected(true);
        setIsStateOrRegionRoleSelected(false);

        // Clear the values when the role changes
        setValue('region', null);
      }
    },
    [setValue, setRoleSelected, setIsStateOrRegionRoleSelected, setIsSiteOrConsultantRoleSelected],
  );

  const onSubmit = async (data: TeamMemberFormData) => {
    const isEmailValid = await validateEmail();
    if (!isEmailValid) {
      setError('email', {
        type: 'manual',
        message: `The email ${email}${
          allowDukeDomain ? `@${DUKE_DOMAIN}` : `@${emailDomain}`
        } is already taken. Please choose a unique email.`,
      });

      setEmailToValidate(false);
      return;
    }

    if (data.role == null) {
      setError('role', { type: 'manual', message: t`Please select a role` });
      return;
    }

    if (member?.id != null) {
      await updateMember.mutateAsync({
        id: member.id,
        first_name: data.first_name,
        last_name: data.last_name,
        role: data.role,
        region: data.region ?? undefined,
        site: data.site ?? undefined,
      });
    } else {
      const resolvedEmail = data.email ? `${data.email}@${emailDomain}` : undefined;

      if (resolvedEmail == null) {
        setError('email', { type: 'manual', message: t`Email is required` });
        return;
      }

      await createMember.mutateAsync({
        firstName: data.first_name,
        lastName: data.last_name,
        email: resolvedEmail,
        role: data.role,
        region: data.region ?? undefined,
        site: data.site ?? undefined,
        providerId: auth.user.tenant.id,
      });
    }

    if (onClose) {
      onClose();
    }
  };

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }} noValidate>
      <VStack spacing={4} align="stretch" p={4}>
        <FormControl isInvalid={!!errors.email} isRequired>
          <FormLabel htmlFor="email">
            <Trans>Email</Trans>
          </FormLabel>
          <InputGroup>
            <Input
              autoFocus
              type="text"
              autoComplete="off"
              readOnly={!!member || readOnly}
              disabled={!!member || readOnly}
              borderRightRadius={!allowDukeDomain && dropdownDomains.length > 1 ? 0 : undefined}
              {...register('email', {
                required: t`Email is required`,
                validate: (value) => {
                  if (value?.includes('@')) {
                    return t`Email cannot contain the '@' symbol. Please enter only the username.`;
                  }

                  const constructedEmail = `${value ?? ''}${allowDukeDomain ? `@${DUKE_DOMAIN}` : `@${emailDomain}`}`;
                  const isValid = emailHasAllowedDomain(allowedDomains, constructedEmail);

                  if (isValid === false) {
                    return t`Email must match one of: ${allowedDomains.map((domain) => `@${domain}`).join(', ')}`;
                  }

                  return true;
                },
                onChange: () => {
                  if (errors.email) {
                    clearErrors('email');
                  }
                },
              })}
            />
            {emailDomain ? (
              allowDukeDomain || dropdownDomains.length <= 1 ? (
                <InputRightAddon>
                  {allowDukeDomain ? `@${DUKE_DOMAIN}` : `@${emailDomain}`}
                </InputRightAddon>
              ) : (
                <Box flex="0 0 40%">
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' }),
                    }}
                    isSearchable={false}
                    isDisabled={!!member || readOnly}
                    options={dropdownDomains.map((domain) => ({
                      label: `@${domain}`,
                      value: domain,
                    }))}
                    value={{ label: `@${emailDomain}`, value: emailDomain ?? '' }}
                    onChange={(option) => {
                      if (option != null) {
                        setSelectedDomain(option.value);
                      }
                    }}
                    chakraStyles={{
                      control: (provided) => ({
                        ...provided,
                        borderLeftRadius: 0,
                      }),
                    }}
                  />
                </Box>
              )
            ) : null}
          </InputGroup>
          <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired isInvalid={!!errors.first_name}>
          <FormLabel htmlFor="first_name">
            <Trans>First name</Trans>
          </FormLabel>
          <Input
            autoComplete="off"
            readOnly={!!member?.deleted_at || readOnly}
            disabled={!!member?.deleted_at || readOnly}
            {...register('first_name', {
              required: t`First name is required`,
              onChange: () => {
                if (errors.first_name) {
                  clearErrors('first_name');
                }
              },
            })}
          />
          <FormErrorMessage>{errors.first_name?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isRequired isInvalid={!!errors.last_name}>
          <FormLabel htmlFor="last_name">
            <Trans>Last name</Trans>
          </FormLabel>
          <Input
            autoComplete="off"
            readOnly={!!member?.deleted_at || readOnly}
            disabled={!!member?.deleted_at || readOnly}
            {...register('last_name', {
              required: t`Last name is required`,
              onChange: () => {
                if (errors.last_name) {
                  clearErrors('last_name');
                }
              },
            })}
          />
          <FormErrorMessage>{errors.last_name?.message}</FormErrorMessage>
        </FormControl>

        {allowRoleSelection ? (
          <FormControl isRequired isInvalid={!!errors.role}>
            <FormLabel htmlFor="role">
              <Trans>Role</Trans>
            </FormLabel>
            {showRoleLocationGroupSelector ? (
              <Controller
                name="role"
                control={control}
                defaultValue={ConsultantRoleSchema.safeParse(member?.role).data ?? null}
                render={({ field }) => (
                  <Select
                    id="role"
                    placeholder={t`Please select role`}
                    options={roleOptions}
                    value={roleOptions?.find(
                      (option: ConsultantSelectOption) => option.value === field.value,
                    )}
                    isDisabled={!!member?.deleted_at || auth.user.id === member?.id || readOnly}
                    onChange={(
                      selectedOption:
                        | MultiValue<ConsultantSelectOption>
                        | SingleValue<ConsultantSelectOption>,
                    ) => {
                      if (
                        selectedOption != null &&
                        'value' in selectedOption &&
                        selectedOption?.label != null &&
                        selectedOption?.value != null
                      ) {
                        field.onChange(selectedOption?.value);
                        handleRoleChange(selectedOption?.value);
                      }
                    }}
                  />
                )}
                rules={{ required: t`Please select a role` }}
              />
            ) : (
              <InputGroup>
                <Input
                  readOnly
                  value={
                    allRoleOptions?.find(
                      (option: ConsultantSelectOption) => option.value === member?.role,
                    )?.label
                  }
                />
                <InputRightAddon>
                  <Button
                    variant="link"
                    isDisabled={!!member?.deleted_at || auth.user.id === member?.id || readOnly}
                    onClick={() => {
                      setShowRoleLocationGroupSelector(true);
                      setValue('role', null);
                    }}
                  >
                    <Trans>Change</Trans>
                  </Button>
                </InputRightAddon>
              </InputGroup>
            )}
            <FormErrorMessage>{errors.role?.message}</FormErrorMessage>
          </FormControl>
        ) : null}

        {roleSelected ? (
          <>
            {isStateOrRegionRoleSelected ? (
              <FormControl isRequired isInvalid={!!errors.region}>
                <FormLabel htmlFor="region">
                  <Trans>Region</Trans>
                </FormLabel>
                <Controller
                  name="region"
                  control={control}
                  render={({ field }) => (
                    <AsyncSelect
                      id="region"
                      name="region"
                      placeholder={t`Please select region`}
                      defaultOptions={siteRegionsOptions}
                      loadOptions={loadSiteRegionsOptions}
                      selectedOptionColorScheme={'brand'}
                      onChange={(val: SelectOption | null) => field.onChange(val?.value)}
                      isLoading={isRegionsPending}
                      isDisabled={siteRegionsOptions.length === 0 || readOnly}
                    />
                  )}
                  rules={{ required: t`Please select a region` }}
                />
                <FormErrorMessage>{errors.region?.message}</FormErrorMessage>
              </FormControl>
            ) : null}

            {isSiteOrConsultantRoleSelected ? (
              <FormControl isRequired isInvalid={!!errors.site}>
                <FormLabel htmlFor="site">
                  <Trans>Site</Trans>
                </FormLabel>
                <Controller
                  name="site"
                  control={control}
                  render={({ field }) => (
                    <AsyncSelect
                      id="site"
                      name="site"
                      placeholder={t`Select site`}
                      defaultOptions={sitesLocationsOptions}
                      loadOptions={loadSitesLocationsOptions}
                      selectedOptionColorScheme={'brand'}
                      onChange={(val: SelectOption | null) => field.onChange(val?.value)}
                      isLoading={isSitesPending}
                      isDisabled={sitesLocationsOptions.length === 0 || readOnly}
                    />
                  )}
                  rules={{ required: t`Please select a site` }}
                />
                <FormErrorMessage>{errors.site?.message}</FormErrorMessage>
              </FormControl>
            ) : null}
          </>
        ) : null}

        {!readOnly ? (
          <Flex justify={'flex-end'}>
            <Button
              right={0}
              type="submit"
              isLoading={isSubmitting}
              isDisabled={
                !!member?.deleted_at || member
                  ? updateMember.isPending || isSubmitting || isLoading
                  : createMember.isPending || isSubmitting || isLoading
              }
            >
              {member ? t`Update team member` : t`Invite team member`}
            </Button>
          </Flex>
        ) : null}
      </VStack>
    </Box>
  );
}
